import React, { FC, useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, Grid } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { RichOption, TIconName } from 'apps/fleet-planner/components/atoms/RichOption'
import { getRoute } from 'apps/fleet-planner/constants'
import { useUserContext, useOnboardingWizardContext } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { SIGNUP_INTENT } from 'apps/fleet-planner/types'
import { updateUserSettings } from 'apps/fleet-planner/utils/api'
import { Button, InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui'
import { OnboardingStepContainer } from './OnboardingStepContainer'

interface IRichOption {
  intentName: SIGNUP_INTENT
  title: string
  description: string
  iconName: TIconName
}

const OPTIONS: IRichOption[] = [
  {
    intentName: SIGNUP_INTENT.getVerified,
    title: 'Get verified',
    description: 'Start here to submit your carrier packet and join our carrier network.',
    iconName: 'Verified',
  },
  {
    intentName: SIGNUP_INTENT.searchLoads,
    title: 'Search for loads',
    description: 'Find well-paying loads that meet your needs.',
    iconName: 'SearchLoads',
  },
  {
    intentName: SIGNUP_INTENT.manageFleet,
    title: 'Manage my fleet',
    description: 'Explore our free solution for managing all of your loads, drivers, and more.',
    iconName: 'ManageFleet',
  },
]

interface IOnboardingStepOptionsProps {
  onSuccess?: () => void
}

/**
 * Step in <OnboardingWizard /> for making user preference selections
 */
export const OnboardingStepOptions: FC<IOnboardingStepOptionsProps> = () => {
  const { trackEvent } = useFMSAnalytics()
  const history = useHistory()
  const { isMobile } = useBreakpointContext()
  const { getUser, userData } = useUserContext()
  const { updateUserDetails } = useUserDetails()
  const { motorCarriers = [] } = userData || {}
  const { firstName = '' } = getUser() || {}
  const { setSelectedOptions, selectedOptions = [] } = useOnboardingWizardContext()

  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [isLoading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    trackEvent('Onboarding', 'ONBOARDING_VIEWED_SIGNUP_INTENT_PAGE')
  }, [trackEvent])

  /**
   * Programmatically generate a callback based on index in list
   */
  const createChangeHandler = (index: SIGNUP_INTENT) => () => {
    trackEvent('Onboarding', 'ONBOARDING_SELECT_OPTION', { intent: index })

    setSelectedOptions(prev => {
      if (prev.includes(index)) {
        return prev.filter(item => index !== item)
      }
      return [...prev, index]
    })
  }

  /**
   * Send PUT request and route the user on success
   */
  const continueHandler = async (): Promise<void> => {
    if (isLoading) return
    try {
      await updateUserSettings({
        settings: {
          signupIntents: [...selectedOptions],
        },
      })

      if (updateUserDetails) {
        await updateUserDetails()
      }

      setErrorMessage(undefined)
      setLoading(false)

      trackEvent('Onboarding', 'ONBOARDING_SUCCESSFULLY_SUBMITTED_FORM_ON_SIGNUP_INTENT_PAGE')

      // On success, send to next route
      if (motorCarriers.length > 0) {
        // Skip the carrier setup step if user already has carriers.
        history.push(getRoute('loading'))
      } else {
        history.push(getRoute('carriers'))
      }
    } catch (e) {
      trackEvent('Onboarding', 'ONBOARDING_FAILED_TO_SUBMIT_FORM_ON_SIGNUP_INTENT_PAGE', e)
      setErrorMessage(e?.message || 'Failed to update user selections.')
      setLoading(false)
    }
  }

  const salutation = firstName ? `, ${firstName}` : ''

  return (
    <OnboardingStepContainer
      textAlign={isMobile ? 'left' : 'center'}
      title={`Welcome${salutation}! How can we help you today?`}
      fadeIn
      subtitle='Select which areas you’d like to get started with today.'>
      <Box mb={4}>
        <Box mb={-2}>
          {OPTIONS.map(({ description, iconName, intentName, title }) => {
            return (
              <Box mb={2} key={title}>
                <RichOption
                  title={title}
                  description={description}
                  iconName={iconName}
                  checked={selectedOptions.includes(intentName)}
                  onChange={createChangeHandler(intentName)}
                  dataTest={`rich-option-${intentName}`}
                />
              </Box>
            )
          })}
        </Box>
      </Box>
      {errorMessage && (
        <Box mb={2}>
          <InlineMessage
            fullWidth
            message='There was an error saving your selections. Please try again.'
            type={INLINE_MESSAGE_VARIANTS.ALERT}
          />
        </Box>
      )}
      <Grid container spacing={3} justifyContent='center'>
        <Grid item xs={12} md={6}>
          <Button
            fullWidth
            color='primary'
            data-test='continue-button'
            onClick={continueHandler}
            disabled={selectedOptions.length === 0 || isLoading}
            size='large'>
            {isLoading ? 'Loading... ' : 'Continue'}
          </Button>
        </Grid>
      </Grid>
    </OnboardingStepContainer>
  )
}
